import { Box, Divider, Typography } from '@mui/material'
import { FeatureFlagIdGrantReasons } from 'c1gen/features'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Usage, Cost, Risk, Status, Analysis, EmploymentStatus, ComplianceFramework } from 'components/core/insights'
import { useFeatureChecker } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { GrantOriginForInsights } from 'components/pages/entitlement/grant-origin/GrantOrigin'
import { TaskValue } from 'lib/actions/task'
import { Flag, Info, ThumbsUp, TriangleAlert } from 'lucide-react'
import { Insight, InsightType } from 'pbts/c1/api/task/v1/insights'
import { createContext, FC, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { insightStyles } from './styles'

type InsightTypeIconProps = {
    insightType: InsightType
}

export const InsightTypeIcon: FC<InsightTypeIconProps> = ({ insightType }) => {
    switch (insightType) {
        case InsightType.POSITIVE:
            return <C1Icon icon={ThumbsUp} sx={sxc(insightStyles.icon, insightStyles.iconPositive)} />
        case InsightType.NEGATIVE:
            return <C1Icon icon={Flag} sx={sxc(insightStyles.icon, insightStyles.iconNegative)} />
        case InsightType.NEUTRAL:
            return <C1Icon icon={Info} sx={sxc(insightStyles.icon, insightStyles.iconNeutral)} />
        case InsightType.ALERT:
            return <C1Icon icon={TriangleAlert} sx={sxc(insightStyles.icon, insightStyles.iconAlert)} />
        default:
            return null
    }
}

type InsightItemProps = {
    insightType: InsightType
    children: ReactNode
}

export const InsightItem: FC<InsightItemProps> = ({ insightType, children }) => {
    return (
        <Box sx={insightStyles.insightItem}>
            <InsightTypeIcon insightType={insightType} />
            {children}
        </Box>
    )
}

type InsightProps = {
    insights: Insight[] | undefined
    task: TaskValue
    displayVariant?: 'compact' | 'expanded'
}

const InsightsDisplayContext = createContext<'compact' | 'expanded'>('expanded')

export const Insights: FC<InsightProps> = ({ insights, task, displayVariant = 'expanded' }) => {
    const hasGrantReasonsFF = useFeatureChecker(FeatureFlagIdGrantReasons)
    const showGrantOrigins = task.type?.taskType.oneofKind === 'certify' && !!hasGrantReasonsFF
    if (insights === undefined) {
        return null
    }
    const data = [] as Array<React.ReactElement | null>
    const sortedInsights = [...insights]
    sortedInsights?.sort((a: Insight, b: Insight): number => {
        return b.type - a.type
    })
    sortedInsights?.forEach((insight, i) => {
        switch (insight.kind.oneofKind) {
            case 'userStatus':
                data.push(
                    <Status key={`status_${i}`} userStatus={insight.kind.userStatus.userStatus} user={task.identityUser || task.user} insightType={insight.type} />,
                )
                break
            case 'employmentStatus':
                data.push(
                    <EmploymentStatus key={`status_${i}`} employmentStatus={insight.kind.employmentStatus.employmentStatus} employmentStatusSources={insight.kind.employmentStatus.employmentStatusSources} user={task.identityUser || task.user} insightType={insight.type} />,
                )
                break
            case 'usage':
                data.push(
                    <Usage key={`usage_${i}`} usage={insight.kind.usage} app={task.app} appUser={task.appUser} insightType={insight.type} />,
                )
                break
            case 'risk':
                data.push(
                    <Risk key={`risk_${i}`} risk={insight.kind.risk} insightType={insight.type} task={task} />,
                )
                break
            case 'complianceFramework':
                data.push(
                    <ComplianceFramework key={`risk_${i}`} complianceFramework={insight.kind.complianceFramework} insightType={insight.type} />,
                )
                break
            case 'cost':
                data.push(
                    <Cost key={`cost_${i}`} cost={insight.kind.cost} insightType={insight.type} />,
                )
                break
            case 'analysis':
                data.push(
                    <Analysis task={task} key={`analysis_${i}`} analysis={insight.kind.analysis} insightType={insight.type} />,
                )
                break
            case 'grantOrigin':
                if (showGrantOrigins) {
                    data.push(
                        <GrantOriginForInsights
                            key={`grantOrigin_${i}`}
                            grantOrigin={insight.kind.grantOrigin}
                            insightType={insight.type}
                        />,
                    )
                }
                break
            default:
        }
    })
    if (data.length === 0) {
        return null
    }
    return <Box mt={displayVariant === 'expanded' ? 2 : 0}>
        <Typography variant={displayVariant === 'expanded' ? 'h4' : 'h6'}><FormattedMessage id="components_task_details_content_tab_details" defaultMessage="Insights" /></Typography>

        <Divider sx={{ my: displayVariant === 'expanded' ? 4 : 2 }} />

        <InsightsDisplayContext.Provider value={displayVariant}>{data}</InsightsDisplayContext.Provider>
    </Box>
}
